import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-align: center;
`;

const ErrorCard = styled.div`
  background: white;
  color: #333;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #d63031;
`;

const Message = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: #666;
  line-height: 1.5;
`;

const Details = styled.details`
  margin-bottom: 2rem;
  text-align: left;
  
  summary {
    cursor: pointer;
    font-weight: 600;
    color: #667eea;
    margin-bottom: 0.5rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  pre {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 8px;
    font-size: 0.8rem;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
    color: #333;
    margin-top: 0.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'secondary' ? `
    background: #f8f9fa;
    color: #333;
    border: 1px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  ` : `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }
  `}
  
  &:active {
    transform: translateY(0);
  }
`;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console and potentially to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Here you could send the error to a logging service
    // this.logErrorToService(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportError = () => {
    const { error, errorInfo } = this.state;
    const errorReport = {
      error: error?.toString(),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    // Create a mailto link with error details
    const subject = 'Relatório de Erro - AI Chat App';
    const body = `Por favor, descreva o que você estava fazendo quando este erro ocorreu:\n\n---\n\nDetalhes do Erro:\n${JSON.stringify(errorReport, null, 2)}`;
    const mailtoLink = `mailto:support@example.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.open(mailtoLink);
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      
      return (
        <ErrorContainer>
          <ErrorCard>
            <ErrorIcon>⚠️</ErrorIcon>
            <Title>Ops! Algo deu errado</Title>
            <Message>
              Desculpe, mas algo inesperado aconteceu. O erro foi registrado 
              e trabalharemos para corrigi-lo o mais rápido possível.
            </Message>
            
            {(error || errorInfo) && (
              <Details>
                <summary>Mostrar detalhes técnicos</summary>
                <pre>
                  {error && error.toString()}
                  {errorInfo && errorInfo.componentStack}
                </pre>
              </Details>
            )}
            
            <ButtonGroup>
              <Button onClick={this.handleReload}>
                Recarregar Página
              </Button>
              <Button variant="secondary" onClick={this.handleGoHome}>
                Ir para o Início
              </Button>
              <Button variant="secondary" onClick={this.handleReportError}>
                Reportar Erro
              </Button>
            </ButtonGroup>
          </ErrorCard>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 